import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import QuickRechargeButton from "@/components/QuickRechargeButton";
import { api, formatApiError, formatEUR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowRight, CheckCircle, XCircle, Receipt, Gift, Info, Wallet } from "@phosphor-icons/react";

const IVA_RATE = 0.21;
const withIva = (n) => Number(n) * (1 + IVA_RATE);

/* ============================================================
 * Recargas de créditos (único producto comprable)
 * ============================================================ */
const RECARGAS = [
    { plan_id: "deposito_100",  amount: 100,  credited: 100,  bonus: 0 },
    { plan_id: "deposito_250",  amount: 250,  credited: 260,  bonus: 10 },
    { plan_id: "deposito_500",  amount: 500,  credited: 525,  bonus: 25 },
    { plan_id: "deposito_1000", amount: 1000, credited: 1080, bonus: 80 },
];

/* Tarifas informativas · precio en € que consume cada acción */
const TARIFAS = [
    { key: "dictamen",         label: "Dictamen Pre-Bancario",      precio: 14,   nota: "9 ratios bancarios + veredicto + simulador Antes/Después" },
    { key: "balance_basico",   label: "Informe Financiero (Básico)", precio: 24,  nota: "1 informe con IA (interpretación + 8 ratios)" },
    { key: "balance_master",   label: "Informe Master",             precio: 44,   nota: "Básico + dictamen bancario + Deuda/EBITDA + DSCR" },
    { key: "modificacion",     label: "Modificación de balance",    precio: 5,    nota: "2ª rectificación y siguientes · 1ª gratuita" },
    { key: "ocr",              label: "OCR de factura",             precio: 0.25, nota: "Por factura procesada en Portal Asesor" },
    { key: "verifactu_est",    label: "Verifactu · empresa/mes",    precio: 12,   nota: "Facturación electrónica AEAT · precio estándar" },
    { key: "verifactu_asesor", label: "Verifactu · asesoría 6+",    precio: 10,   nota: "Para asesorías desde la 6ª empresa activa" },
];

export default function Payments() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);
    const [confirmPlan, setConfirmPlan] = useState(null);
    const [customAmount, setCustomAmount] = useState("");
    const rawNext = params.get("next") || "";
    const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";

    useEffect(() => {
        if (!success || !nextPath) return;
        const t = setTimeout(() => nav(nextPath), 3500);
        return () => clearTimeout(t);
    }, [success, nextPath, nav]);

    useEffect(() => {
        const sid = params.get("session_id");
        if (sid) {
            (async () => {
                try {
                    const { data } = await api.get(`/payments/status/${sid}`);
                    if (data.payment_status === "paid") {
                        setSuccess(data);
                        await refresh?.();
                    }
                } catch (e) {
                    setError(formatApiError(e, "Error al verificar el pago"));
                }
            })();
        }
    }, [params, refresh]);

    async function handleCheckout(planId, customAmt) {
        setError(""); setLoading(planId);
        try {
            const body = { plan_id: planId, origin_url: window.location.origin };
            if (customAmt) body.custom_amount = customAmt;
            const { data } = await api.post("/payments/checkout", body);
            window.location.href = data.url;
        } catch (e) {
            setError(formatApiError(e, "No se pudo iniciar el pago"));
            setLoading(null);
        }
    }

    const openConfirm = (planId, label, base) => setConfirmPlan({ plan_id: planId, label, base });

    function onCustomRecharge() {
        const val = Number(customAmount);
        if (!val || val < 100) {
            setError("La recarga mínima es 100 €");
            return;
        }
        openConfirm("deposito_custom", `Recarga personalizada ${val} €`, val);
    }

    const credits = Number(user?.credits_eur || 0);

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-6xl mx-auto w-full px-6 md:px-12 py-12">
                <div className="mb-10 max-w-3xl">
                    <div className="micro-label mb-3">— Créditos unificados</div>
                    <h1 className="serif text-4xl md:text-5xl mb-4 leading-[1.1]">
                        Un solo <span className="italic text-[var(--gf-gold)]">monedero</span> para todos los servicios
                    </h1>
                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">
                        Recarga tus créditos y consúmelos donde quieras: Balances con IA, OCR de facturas, Modificaciones o suscripción Verifactu.
                        Sin caducidad. Todos los importes son <strong>bases sin IVA</strong>; al pasar por caja se añade el 21 %.
                    </p>
                </div>

                {success && (
                    <div className="gf-card border-l-4 border-l-[var(--gf-success)] bg-green-50 mb-8" data-testid="success-banner">
                        <div className="flex items-start gap-3">
                            <CheckCircle size={24} weight="fill" className="text-[var(--gf-success)] flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-semibold text-[var(--gf-success)]">¡Pago confirmado!</div>
                                <p className="text-sm text-[var(--gf-text-secondary)]">
                                    Total cobrado: <strong>{formatEUR((success.amount_total || 0) / 100, 2)}</strong>. Ya puedes usar tus créditos.
                                </p>
                                {nextPath && (
                                    <p className="text-sm mt-2" data-testid="success-next">
                                        Volviendo a donde estabas en unos segundos…{" "}
                                        <Link to={nextPath} className="text-[var(--gf-navy)] font-semibold underline underline-offset-4" data-testid="success-next-link">Continuar ahora →</Link>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="gf-card border-l-4 border-l-[var(--gf-danger)] bg-red-50 mb-8" data-testid="error-banner">
                        <div className="flex items-start gap-3">
                            <XCircle size={20} weight="fill" className="text-[var(--gf-danger)] mt-0.5" />
                            <span className="text-sm">{error}</span>
                        </div>
                    </div>
                )}

                {/* Saldo actual */}
                {user && (
                    <div className="mb-14" data-testid="credits-summary">
                        <div className="bg-[var(--gf-navy)] text-white p-6 md:p-8 border-t-4 border-t-[var(--gf-gold)]">
                            <div className="flex items-center gap-2 mb-2">
                                <Wallet size={20} weight="light" className="text-[var(--gf-gold)]" />
                                <span className="micro-label text-white/70">— Créditos disponibles</span>
                            </div>
                            <div className="serif text-5xl md:text-6xl mono text-[var(--gf-gold)]" data-testid="saldo-creditos">
                                {formatEUR(credits, 2)}
                            </div>
                            <div className="text-xs text-white/70 mt-2">
                                Consumibles en cualquier módulo · sin caducidad
                            </div>
                        </div>
                    </div>
                )}

                {/* Recargas */}
                <section className="mb-16" data-testid="section-recargas">
                    <div className="mb-6">
                        <div className="micro-label text-[var(--gf-gold)] mb-2">— Recarga tus créditos</div>
                        <h2 className="serif text-3xl md:text-4xl text-[var(--gf-navy)]">Elige un tramo</h2>
                        <p className="text-sm text-[var(--gf-text-secondary)] mt-2 max-w-3xl">
                            Los tramos grandes incluyen bonus de saldo. Puedes también recargar un importe libre (mínimo 100 €).
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4">
                        {RECARGAS.map((t, idx) => {
                            const isPromo = t.bonus > 0;
                            const isFeatured = idx === 2;
                            return (
                                <div
                                    key={t.plan_id}
                                    className={`border bg-white p-5 flex flex-col ${
                                        isFeatured
                                            ? "border-[var(--gf-gold)] border-t-4 border-t-[var(--gf-gold)] relative"
                                            : "border-[var(--gf-border)] border-t-4 border-t-[var(--gf-navy)]"
                                    }`}
                                    data-testid={`recarga-tramo-${t.amount}`}
                                >
                                    {isFeatured && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--gf-gold)] text-white text-[9px] uppercase tracking-widest font-semibold px-2 py-1">
                                            Popular
                                        </div>
                                    )}
                                    <div className="micro-label mb-2">— Recarga</div>
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="serif text-3xl mono text-[var(--gf-navy)]">{t.amount} €</span>
                                    </div>
                                    <div className="text-[10px] text-[var(--gf-text-muted)] mb-3 mono">
                                        {formatEUR(withIva(t.amount), 2)} IVA incluido
                                    </div>
                                    <div className={`text-sm mb-4 ${isPromo ? "text-[var(--gf-gold)] font-semibold" : "text-[var(--gf-text-secondary)]"}`}>
                                        {isPromo ? (
                                            <span className="flex items-center gap-1">
                                                <Gift size={14} weight="fill" />
                                                Recibes {t.credited} € (+{t.bonus} € bonus)
                                            </span>
                                        ) : (
                                            <span>Recibes {t.credited} € en tu saldo</span>
                                        )}
                                    </div>
                                    <div className="mt-auto mb-3" />
                                    <button
                                        className={isFeatured ? "btn-gold text-xs w-full justify-center" : "btn-outline text-xs w-full justify-center"}
                                        onClick={() => openConfirm(t.plan_id, `Recarga ${t.amount} € (+${t.bonus} € bonus)`, t.amount)}
                                        disabled={loading === t.plan_id}
                                        data-testid={`buy-recarga-${t.amount}`}
                                    >
                                        Recargar <ArrowRight size={12} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Recargas rápidas · importe exacto de un producto */}
                    <div className="mt-6 border border-[var(--gf-gold)]/50 bg-[var(--gf-gold)]/5 p-5" data-testid="recarga-rapida-block">
                        <div className="micro-label mb-1">— Recargas rápidas</div>
                        <div className="text-sm text-[var(--gf-text-secondary)] mb-4">
                            ¿Sólo necesitas un informe? Recarga el importe exacto y vuelve a tu trabajo en un clic (sin bonus, sin caducidad).
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { amount: 5, label: "Modificación" },
                                { amount: 14, label: "Dictamen Pre-Bancario" },
                                { amount: 24, label: "Informe Financiero" },
                                { amount: 44, label: "Informe Master" },
                            ].map((q) => (
                                <div key={q.amount} className="bg-white border border-[var(--gf-border)] p-3 flex flex-col gap-2" data-testid={`recarga-rapida-${q.amount}`}>
                                    <div className="text-[11px] uppercase tracking-widest text-[var(--gf-text-muted)]">{q.label}</div>
                                    <div className="serif text-2xl mono text-[var(--gf-navy)]">{q.amount} <span className="text-xs text-[var(--gf-text-muted)]">créditos</span></div>
                                    <QuickRechargeButton amount={q.amount} nextPath="/pagos" label={`Recargar · ${formatEUR(withIva(q.amount), 2)}`} className="btn-outline text-xs justify-center" testId={`buy-rapida-${q.amount}`} onError={(m) => setError(m)} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Importe libre */}
                    <div className="mt-6 border border-dashed border-[var(--gf-border)] bg-white p-5" data-testid="recarga-custom-block">
                        <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
                            <div className="flex-1">
                                <div className="micro-label mb-1">— Otro importe</div>
                                <div className="text-sm text-[var(--gf-text-secondary)] mb-3">
                                    Recarga la cantidad exacta que necesites (mínimo 100 €, sin bonus).
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1 max-w-xs">
                                        <input
                                            type="number"
                                            min="100"
                                            step="1"
                                            className="gf-input mono pr-8"
                                            placeholder="Ej. 350"
                                            value={customAmount}
                                            onChange={(e) => setCustomAmount(e.target.value)}
                                            data-testid="recarga-custom-input"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--gf-text-muted)] text-sm">€</span>
                                    </div>
                                    <button
                                        className="btn-outline text-xs"
                                        onClick={onCustomRecharge}
                                        disabled={loading === "deposito_custom" || !customAmount || Number(customAmount) < 100}
                                        data-testid="buy-recarga-custom"
                                    >
                                        Recargar <ArrowRight size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tarifas · consumo */}
                <section className="mb-16" data-testid="section-tarifas">
                    <div className="mb-6">
                        <div className="micro-label text-[var(--gf-gold)] mb-2">— Tarifas de consumo</div>
                        <h2 className="serif text-3xl md:text-4xl text-[var(--gf-navy)]">Cuánto descuenta cada servicio</h2>
                        <p className="text-sm text-[var(--gf-text-secondary)] mt-2 max-w-3xl">
                            Estas tarifas se aplican automáticamente sobre tu saldo de créditos al usar cada módulo.
                        </p>
                    </div>

                    <div className="bg-white border border-[var(--gf-border)] overflow-hidden">
                        <table className="w-full text-sm" data-testid="tarifas-table">
                            <thead className="bg-[var(--gf-navy)] text-white">
                                <tr className="text-[10px] uppercase tracking-widest">
                                    <th className="text-left px-4 py-3 font-medium">Servicio</th>
                                    <th className="text-right px-4 py-3 font-medium">Precio</th>
                                    <th className="text-left px-4 py-3 font-medium">Detalle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {TARIFAS.map((t) => (
                                    <tr key={t.key} className="border-t border-[var(--gf-border)]" data-testid={`tarifa-${t.key}`}>
                                        <td className="px-4 py-3 font-semibold text-[var(--gf-navy)]">{t.label}</td>
                                        <td className="px-4 py-3 text-right mono font-semibold whitespace-nowrap">
                                            {t.precio < 1 ? `${t.precio.toFixed(2).replace(".", ",")} €` : `${t.precio} €`}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[var(--gf-text-secondary)]">{t.nota}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 bg-[var(--gf-subtle)] border-l-4 border-l-[var(--gf-gold)] p-4 flex items-start gap-3 text-sm">
                        <Info size={18} className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                        <div className="text-[var(--gf-text-secondary)] leading-relaxed">
                            <strong className="text-[var(--gf-navy)]">Verifactu:</strong> la suscripción se cobra automáticamente al emitir la 1ª factura del mes por cada empresa activada.
                            Si eres asesoría, a partir de la 6ª empresa el precio baja a 10 €/mes por empresa.
                        </div>
                    </div>
                </section>

                <div className="text-center text-xs text-[var(--gf-text-muted)] max-w-2xl mx-auto">
                    Pago seguro a través de Stripe · Recibirás factura con desglose Base + IVA en tu email.
                </div>
            </main>

            {confirmPlan && (
                <IVAConfirmModal
                    plan={confirmPlan}
                    onCancel={() => setConfirmPlan(null)}
                    onConfirm={() => {
                        const customAmt = confirmPlan.plan_id === "deposito_custom" ? confirmPlan.base : null;
                        handleCheckout(confirmPlan.plan_id, customAmt);
                        setConfirmPlan(null);
                    }}
                    loading={loading === confirmPlan.plan_id}
                />
            )}

            <Footer />
            <WhatsAppFab />
        </div>
    );
}

/* ============================================================ */
/*  Modal confirmación con desglose IVA                          */
/* ============================================================ */
function IVAConfirmModal({ plan, onCancel, onConfirm, loading }) {
    const base = Number(plan.base) || 0;
    const iva = base * IVA_RATE;
    const total = base + iva;
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--gf-navy)]/70 backdrop-blur-sm px-4"
            onClick={(e) => { if (e.target === e.currentTarget && !loading) onCancel(); }}
            data-testid="iva-modal"
        >
            <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="bg-white max-w-md w-full shadow-2xl border-t-4 border-t-[var(--gf-gold)]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-2">
                        <Receipt size={18} weight="light" className="text-[var(--gf-navy)]" />
                        <span className="micro-label">— Resumen de tu compra</span>
                    </div>
                    <h3 className="serif text-2xl text-[var(--gf-navy)] mb-6">{plan.label}</h3>

                    <div className="border border-[var(--gf-border)] divide-y divide-[var(--gf-border)] mb-6">
                        <RowKV label="Concepto" value={plan.label} />
                        <RowKV label="Base imponible" value={formatEUR(base, 2)} mono />
                        <RowKV label="IVA (21%)" value={formatEUR(iva, 2)} mono />
                        <div className="flex justify-between items-center px-4 py-3 bg-[var(--gf-subtle)]">
                            <span className="font-semibold text-[var(--gf-navy)]">TOTAL A PAGAR</span>
                            <span className="serif text-2xl mono text-[var(--gf-navy)]" data-testid="modal-total">{formatEUR(total, 2)}</span>
                        </div>
                    </div>

                    <div className="text-[11px] text-[var(--gf-text-muted)] leading-relaxed mb-6">
                        Tras el pago recibirás factura con desglose por email. Pago seguro a través de Stripe.
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                        <button type="button" onClick={onCancel} disabled={loading} className="btn-outline text-sm justify-center" data-testid="modal-cancel">
                            Cancelar
                        </button>
                        <button type="button" onClick={onConfirm} disabled={loading} className="btn-gold justify-center" data-testid="modal-confirm">
                            {loading ? "Redirigiendo a Stripe…" : (<>Pagar {formatEUR(total, 2)} <ArrowRight size={14} /></>)}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

function RowKV({ label, value, mono }) {
    return (
        <div className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-[var(--gf-text-secondary)]">{label}</span>
            <span className={mono ? "mono" : ""}>{value}</span>
        </div>
    );
}
